import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarRange,
  CheckCircle2,
  ClipboardList,
  History,
  Info,
  MapPinned,
  RefreshCw,
  UserCheck,
} from "lucide-react";
import { StatCard } from "../../components/cards/StatCard";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { useSort, SortHeader, CollapsibleCardHeader } from "../../components/ui/sortable";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";
import {
  fetchOfficerPerformance,
  fetchLocationCompliance,
  fetchMissedPatrols,
  type OfficerPerformanceResponse,
  type LocationComplianceResponse,
  type MissedPatrolsResponse,
} from "../../services/patrolReports";
import {
  OfficerDistribution,
  PercentileBadge,
  metricDef,
  percentileRankOf,
  type MetricKey,
} from "../../components/officers/OfficerDistribution";

/**
 * Patrol Reports — three views over the patrol record:
 *
 *   Officers    recorded activity per officer (shifts, scans, checkpoints)
 *   Coverage    per-checkpoint scan coverage against its rule, over a window
 *   History     compliance alerts the sweep raised, and how they resolved
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FRAMING — the same rule as the Compliance page, and it matters more here
 * because this page shows names.
 *
 * These are reports of the record, for the supervisor's judgement. They are
 * not a ranking, a quota, or a work assignment. Every API response carries an
 * `advisory` explaining what the numbers can and cannot show — notably that
 * unattributed scans exist (scanning while not signed on) and that duties
 * which produce no scans are invisible here. That advisory is rendered at the
 * top of each tab. Do not remove it, and do not add UI that turns these
 * figures into scores, leaderboards, or targets. Column sorting exists so a
 * supervisor can explore the record, not to produce a league table.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type Tab = "officers" | "coverage" | "history";

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => isoDay(new Date(Date.now() - n * 86400_000));

const STATUS_PILLS: Record<string, string> = {
  OVERDUE: "bg-red-100 text-red-800 border-red-200",
  DUE_SOON: "bg-amber-100 text-amber-800 border-amber-200",
  OK: "bg-green-100 text-green-800 border-green-200",
  NEVER_SCANNED: "bg-gray-100 text-gray-600 border-gray-200",
};

function AdvisoryBanner({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
      <p className="text-sm leading-relaxed text-blue-900">{text}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center p-12">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
    </div>
  );
}

interface PatrolReportsPageProps {
  /**
   * True when rendered as the top section of the Officers page (its permanent
   * home — /supervisor/patrol-reports redirects there). Drops the page padding
   * and renders a section heading instead of a page heading.
   */
  embedded?: boolean;
}

export const PatrolReportsPage: React.FC<PatrolReportsPageProps> = ({ embedded = false }) => {
  const { t, lang } = useTranslation();

  const [tab, setTab] = useState<Tab>("officers");
  // Metric driving both the distribution chart and the table's Percentile column.
  const [metric, setMetric] = useState<MetricKey>("scansPerHour");
  const [hospitalId, setHospitalId] = useStickyHospital();
  const [start, setStart] = useState(daysAgo(13));
  const [end, setEnd] = useState(daysAgo(0));
  const [error, setError] = useState<string | null>(null);

  const [officers, setOfficers] = useState<OfficerPerformanceResponse | null>(null);
  const [coverage, setCoverage] = useState<LocationComplianceResponse | null>(null);
  const [history, setHistory] = useState<MissedPatrolsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // Collapse state per long list. Default open — collapsing is for getting past
  // a list, not for hiding it.
  const [officersOpen, setOfficersOpen] = useState(true);
  const [coverageOpen, setCoverageOpen] = useState(true);
  const [rollupOpen, setRollupOpen] = useState(true);
  const [logOpen, setLogOpen] = useState(true);
  const navigate = useNavigate();

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(lang, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [lang]
  );
  const fmt = useCallback(
    (iso: string | null | undefined) => (iso ? dateFmt.format(new Date(iso)) : "—"),
    [dateFmt]
  );

  const load = useCallback(async () => {
    if (start > end) {
      setError(t("patrolReports.badRange", "Start date must be on or before end date"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (tab === "officers") {
        setOfficers(await fetchOfficerPerformance({ hospitalId, start, end }));
      } else if (tab === "coverage") {
        setCoverage(await fetchLocationCompliance({ hospitalId, start, end }));
      } else {
        setHistory(await fetchMissedPatrols({ hospitalId, start, end }));
      }
    } catch (err: any) {
      setError(err?.message || t("patrolReports.loadFailed", "Could not load report"));
    } finally {
      setLoading(false);
    }
  }, [tab, hospitalId, start, end, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Sorting. Click a header to sort by that column (descending first);
  // no sort selected = the API's default ordering.
  const officerSort = useSort(officers?.officers ?? [], {
    officer: (o) => o.name,
    site: (o) => o.hospital_name,
    shifts: (o) => o.shifts,
    shift_time: (o) => o.shift_minutes,
    scans: (o) => o.scans,
    checkpoints: (o) => o.distinct_checkpoints,
    scans_hr: (o) => o.scans_per_hour,
    last_scan: (o) => o.last_scan_at,
    // Percentile sorts by the selected metric's value (monotonic with percentile);
    // officers with no value for the metric sort to the bottom.
    percentile: (o) => metricDef(metric).value(o) ?? Number.NEGATIVE_INFINITY,
  });

  // Percentile of each officer for the currently selected metric, computed over
  // the officers that actually have a value for it (nulls excluded), so the
  // table column auto-populates and updates the moment the metric changes.
  const md = metricDef(metric);
  const metricSortedVals = useMemo(() => {
    return (officers?.officers ?? [])
      .map((o) => md.value(o))
      .filter((v): v is number => v != null && !isNaN(v))
      .sort((a, b) => a - b);
  }, [officers, metric]); // eslint-disable-line react-hooks/exhaustive-deps
  const pctFor = (o: OfficerPerformanceResponse["officers"][number]): number | null => {
    const v = md.value(o);
    return v == null || isNaN(v) ? null : percentileRankOf(metricSortedVals, v);
  };

  // Clicking a quantitative column header also points the Percentile column (and
  // the Distribution above it) at that metric — so "Percentile · Scans / hr"
  // becomes "Percentile · Scans", "Percentile · Checkpoints", etc. The percentile
  // shown always reflects the column you're reading. Non-metric columns
  // (Officer, Site, Percentile itself, Last Scan) just sort.
  const COLUMN_METRIC: Partial<Record<string, MetricKey>> = {
    scans: "scans",
    checkpoints: "checkpoints",
    scans_hr: "scansPerHour",
    shifts: "shifts",
    shift_time: "shiftHours",
  };
  const toggleColumn = (k: string) => {
    officerSort.toggle(k);
    const mk = COLUMN_METRIC[k];
    if (mk) setMetric(mk);
  };

  const coverageSort = useSort(coverage?.checkpoints ?? [], {
    checkpoint: (c) => c.name,
    days: (c) => c.coverage_rate,
    scans: (c) => c.scans,
    expected: (c) => c.expected_scans,
    status: (c) => c.status,
  });

  const rollupSort = useSort(history?.by_location ?? [], {
    checkpoint: (r) => r.location_name,
    alerts: (r) => r.alerts,
    open: (r) => r.open,
    worst: (r) => r.worst_minutes_overdue,
    avg_resolve: (r) => r.avg_minutes_to_resolve,
    latest: (r) => r.latest_alert_at,
  });

  const minutesToLabel = (m: number | null | undefined) => {
    if (m == null) return "—";
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    if (h === 0) return `${rem}m`;
    if (rem === 0) return `${h}h`;
    return `${h}h ${rem}m`;
  };

  const TABS: Array<{ id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: "officers", label: t("patrolReports.tabOfficers", "Officers"), icon: UserCheck },
    { id: "coverage", label: t("patrolReports.tabCoverage", "Coverage"), icon: MapPinned },
    { id: "history", label: t("patrolReports.tabHistory", "Alert History"), icon: History },
  ];

  return (
    <div className={cn("space-y-4 md:space-y-6", !embedded && "p-4 md:p-6")}>
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800 md:text-2xl">
            {embedded
              ? t("patrolReports.embeddedTitle", "Officer Patrol Reports")
              : t("patrolReports.title", "Patrol Reports")}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {t(
              "patrolReports.subtitle",
              "Recorded patrol activity by officer, checkpoint coverage, and compliance alert history."
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HospitalSelector selectedHospitalId={hospitalId} onHospitalChange={setHospitalId} />
          <div className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2">
            <CalendarRange className="h-4 w-4 text-gray-500" />
            <input
              type="date"
              value={start}
              max={end}
              onChange={(e) => setStart(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
              aria-label={t("patrolReports.startDate", "Start date")}
            />
            <span className="text-gray-400">–</span>
            <input
              type="date"
              value={end}
              min={start}
              onChange={(e) => setEnd(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
              aria-label={t("patrolReports.endDate", "End date")}
            />
          </div>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-colors",
              tab === tb.id
                ? "bg-white text-blue-700 shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            )}
          >
            <tb.icon className="h-4 w-4" />
            {tb.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <span>{error}</span>
          <button onClick={() => void load()} className="font-semibold underline">
            {t("common.retry", "Retry")}
          </button>
        </div>
      )}

      {/* ── Officers ── */}
      {tab === "officers" && (
        <>
          {loading || !officers ? (
            <Spinner />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                  title={t("patrolReports.totalScans", "Scans in Window")}
                  value={officers.totals.scans}
                  icon={ClipboardList}
                  iconColor="info"
                />
                <StatCard
                  title={t("patrolReports.attributed", "Attributed to an Officer")}
                  value={officers.totals.attributed_scans}
                  icon={UserCheck}
                  iconColor="success"
                />
                <StatCard
                  title={t("patrolReports.unattributed", "Unattributed")}
                  value={officers.totals.unattributed_scans}
                  subtitle={t(
                    "patrolReports.unattributedSub",
                    "Scanned while no officer was signed on"
                  )}
                  icon={AlertTriangle}
                  iconColor={officers.totals.unattributed_scans > 0 ? "warning" : "primary"}
                />
                <StatCard
                  title={t("patrolReports.officersListed", "Officers Listed")}
                  value={officers.totals.officers}
                  icon={CheckCircle2}
                  iconColor="primary"
                />
              </div>

              {/* Distribution + summary for the selected metric, sitting above the
                  table. The metric chosen here also drives the table's Percentile
                  column below. */}
              <OfficerDistribution officers={officers.officers} metric={metric} onMetricChange={setMetric} />

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <CollapsibleCardHeader
                  title={t("patrolReports.officersTableTitle", "Recorded Activity by Officer")}
                  count={officers.officers.length}
                  open={officersOpen}
                  onToggle={() => setOfficersOpen((o) => !o)}
                  collapseLabel={t("common.collapse", "Collapse")}
                  expandLabel={t("common.expand", "Expand")}
                />
                {officersOpen &&
                  (officers.officers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                      <UserCheck className="mb-4 h-12 w-12 text-gray-300" />
                      <p className="text-gray-500">
                        {t("patrolReports.noOfficers", "No officers in this selection.")}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-[36rem] overflow-x-auto overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 border-b border-gray-200 bg-gray-50">
                          <tr>
                            <SortHeader label={t("patrolReports.colOfficer", "Officer")} k="officer" sort={officerSort.sort} onToggle={toggleColumn} />
                            <SortHeader label={t("patrolReports.colSite", "Site")} k="site" sort={officerSort.sort} onToggle={toggleColumn} />
                            <SortHeader label={t("patrolReports.colShifts", "Shifts")} k="shifts" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={t("patrolReports.colShiftTime", "Shift Time")} k="shift_time" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={t("patrolReports.colScans", "Scans")} k="scans" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={t("patrolReports.colCheckpoints", "Checkpoints")} k="checkpoints" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={t("patrolReports.colScansHr", "Scans / hr")} k="scans_hr" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={`${t("patrolReports.colPercentile", "Percentile")} · ${md.label}`} k="percentile" sort={officerSort.sort} onToggle={toggleColumn} right />
                            <SortHeader label={t("patrolReports.colLastScan", "Last Scan")} k="last_scan" sort={officerSort.sort} onToggle={toggleColumn} right />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {officerSort.sorted.map((o) => (
                            <tr key={o.officer_id} className="transition-colors hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1.5">
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        onClick={() => navigate(`/supervisor/officers/${o.officer_id}`)}
                                        className="text-left font-medium text-[#3B7BBE] hover:underline"
                                        title={t("officers.viewProfile", "View officer profile")}
                                      >
                                        {o.name}
                                      </button>
                                      {o.status === "PENDING" && (
                                        <span
                                          className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
                                          title={t("officers.pendingCreditedHint", "Self-enrolled — scans are credited now; awaiting supervisor approval")}
                                        >
                                          {t("officers.status.PENDING", "Pending")}
                                        </span>
                                      )}
                                    </div>
                                    <div className="font-mono text-xs text-gray-500">{o.n_number}</div>
                                  </div>
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/supervisor/scan-map?officerId=${o.officer_id}&officerName=${encodeURIComponent(o.name)}`
                                      )
                                    }
                                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                    title={t("officerMap.open", "View scan history on the Scan Map")}
                                    aria-label={t("officerMap.open", "View scan history on the Scan Map")}
                                  >
                                    <MapPinned className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {o.hospital_name || "—"}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {o.shifts}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {minutesToLabel(o.shift_minutes)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                                {o.scans}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {o.distinct_checkpoints}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {o.scans_per_hour ?? "—"}
                              </td>
                              <td className="px-4 py-3 text-right text-sm">
                                <PercentileBadge p={pctFor(o)} />
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                                {fmt(o.last_scan_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
              </div>

              {/* Advisory sits below the record, between the Recorded Activity
                  table and the Officers roster that follows on the page. */}
              <AdvisoryBanner text={officers.advisory} />
            </>
          )}
        </>
      )}

      {/* ── Coverage ── */}
      {tab === "coverage" && (
        <>
          {coverage && <AdvisoryBanner text={coverage.advisory} />}
          {loading || !coverage ? (
            <Spinner />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard
                  title={t("patrolReports.fullyCovered", "Covered Every Day")}
                  value={coverage.summary.fully_covered}
                  subtitle={`${t("patrolReports.of", "of")} ${coverage.summary.checkpoints}`}
                  icon={CheckCircle2}
                  iconColor="success"
                />
                <StatCard
                  title={t("patrolReports.partiallyCovered", "Covered Some Days")}
                  value={coverage.summary.partially_covered}
                  icon={MapPinned}
                  iconColor="warning"
                />
                <StatCard
                  title={t("patrolReports.uncovered", "No Scans in Window")}
                  value={coverage.summary.uncovered}
                  icon={AlertTriangle}
                  iconColor={coverage.summary.uncovered > 0 ? "danger" : "primary"}
                />
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <CollapsibleCardHeader
                  title={t("patrolReports.coverageTitle", "Checkpoint Coverage")}
                  count={coverage.checkpoints.length}
                  open={coverageOpen}
                  onToggle={() => setCoverageOpen((o) => !o)}
                  collapseLabel={t("common.collapse", "Collapse")}
                  expandLabel={t("common.expand", "Expand")}
                  subtitle={t("patrolReports.coverageSort", "Least-covered checkpoints first.")}
                />
                {coverageOpen && (
                  <div className="max-h-[36rem] overflow-x-auto overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 border-b border-gray-200 bg-gray-50">
                        <tr>
                          <SortHeader label={t("patrolReports.colCheckpoint", "Checkpoint")} k="checkpoint" sort={coverageSort.sort} onToggle={coverageSort.toggle} />
                          <SortHeader label={t("patrolReports.colDaysCovered", "Days Covered")} k="days" sort={coverageSort.sort} onToggle={coverageSort.toggle} />
                          <SortHeader label={t("patrolReports.colScans", "Scans")} k="scans" sort={coverageSort.sort} onToggle={coverageSort.toggle} right />
                          <SortHeader label={t("patrolReports.colExpected", "Expected")} k="expected" sort={coverageSort.sort} onToggle={coverageSort.toggle} right />
                          <SortHeader label={t("patrolReports.colStatusNow", "Status Now")} k="status" sort={coverageSort.sort} onToggle={coverageSort.toggle} />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {coverageSort.sorted.map((c) => (
                          <tr key={c.location_id} className="transition-colors hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-800">{c.name}</div>
                              <div className="text-xs text-gray-500">
                                {[c.hospital_name, c.place].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-24 overflow-hidden rounded-sm bg-gray-100">
                                  <div
                                    className={cn(
                                      "h-full rounded-sm",
                                      c.coverage_rate >= 1
                                        ? "bg-green-500"
                                        : c.coverage_rate > 0
                                          ? "bg-amber-500"
                                          : "bg-red-500"
                                    )}
                                    style={{ width: `${Math.round(c.coverage_rate * 100)}%` }}
                                  />
                                </div>
                                <span className="whitespace-nowrap text-sm text-gray-700">
                                  {c.days_covered}/{c.days_in_window}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                              {c.scans}
                            </td>
                            <td className="px-4 py-3 text-right text-sm text-gray-500">
                              {c.expected_scans}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={cn(
                                  "inline-block whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium",
                                  STATUS_PILLS[c.status]
                                )}
                              >
                                {t(`compliance.status.${c.status}`, c.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ── History ── */}
      {tab === "history" && (
        <>
          {history && <AdvisoryBanner text={history.advisory} />}
          {loading || !history ? (
            <Spinner />
          ) : (
            <>
              {history.history_note && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  {history.history_note}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard
                  title={t("patrolReports.alertsRaised", "Alerts in Window")}
                  value={history.summary.alerts}
                  icon={History}
                  iconColor="info"
                />
                <StatCard
                  title={t("patrolReports.alertsOpen", "Still Open")}
                  value={history.summary.open}
                  icon={AlertTriangle}
                  iconColor={history.summary.open > 0 ? "danger" : "primary"}
                />
                <StatCard
                  title={t("patrolReports.alertsResolved", "Resolved")}
                  value={history.summary.resolved}
                  icon={CheckCircle2}
                  iconColor="success"
                />
              </div>

              {history.by_location.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <CollapsibleCardHeader
                    title={t("patrolReports.repeatTitle", "Checkpoints Raising Alerts Most Often")}
                    count={history.by_location.length}
                    open={rollupOpen}
                    onToggle={() => setRollupOpen((o) => !o)}
                    collapseLabel={t("common.collapse", "Collapse")}
                    expandLabel={t("common.expand", "Expand")}
                  />
                  {rollupOpen && (
                    <div className="max-h-[28rem] overflow-x-auto overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 border-b border-gray-200 bg-gray-50">
                          <tr>
                            <SortHeader label={t("patrolReports.colCheckpoint", "Checkpoint")} k="checkpoint" sort={rollupSort.sort} onToggle={rollupSort.toggle} />
                            <SortHeader label={t("patrolReports.colAlerts", "Alerts")} k="alerts" sort={rollupSort.sort} onToggle={rollupSort.toggle} right />
                            <SortHeader label={t("patrolReports.colOpen", "Open")} k="open" sort={rollupSort.sort} onToggle={rollupSort.toggle} right />
                            <SortHeader label={t("patrolReports.colWorst", "Worst Overdue")} k="worst" sort={rollupSort.sort} onToggle={rollupSort.toggle} right />
                            <SortHeader label={t("patrolReports.colAvgResolve", "Avg. to Resolve")} k="avg_resolve" sort={rollupSort.sort} onToggle={rollupSort.toggle} right />
                            <SortHeader label={t("patrolReports.colLatest", "Latest")} k="latest" sort={rollupSort.sort} onToggle={rollupSort.toggle} right />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {rollupSort.sorted.map((r) => (
                            <tr
                              key={r.location_id ?? "none"}
                              className="transition-colors hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-800">
                                  {r.location_name ?? "—"}
                                </div>
                                <div className="text-xs text-gray-500">{r.hospital_name}</div>
                              </td>
                              <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                                {r.alerts}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">{r.open}</td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {minutesToLabel(r.worst_minutes_overdue)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-700">
                                {minutesToLabel(r.avg_minutes_to_resolve)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                                {fmt(r.latest_alert_at)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <CollapsibleCardHeader
                  title={t("patrolReports.alertLog", "Alert Log")}
                  count={history.alerts.length}
                  open={logOpen}
                  onToggle={() => setLogOpen((o) => !o)}
                  collapseLabel={t("common.collapse", "Collapse")}
                  expandLabel={t("common.expand", "Expand")}
                />
                {logOpen &&
                  (history.alerts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                      <CheckCircle2 className="mb-4 h-12 w-12 text-gray-300" />
                      <p className="text-gray-500">
                        {t("patrolReports.noAlerts", "No alerts in this window.")}
                      </p>
                    </div>
                  ) : (
                    <ul className="max-h-96 divide-y divide-gray-200 overflow-y-auto">
                      {history.alerts.map((a) => (
                        <li key={a.id} className="flex items-start gap-3 px-6 py-3">
                          <AlertTriangle
                            className={cn(
                              "mt-0.5 h-4 w-4 flex-shrink-0",
                              a.severity === "CRITICAL" ? "text-red-600" : "text-amber-600"
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-800">{a.message}</p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {[a.hospital_name, a.location_name].filter(Boolean).join(" · ")}
                              {" — "}
                              {fmt(a.created_at)}
                              {a.resolved && a.minutes_to_resolve != null && (
                                <>
                                  {" · "}
                                  {t("patrolReports.resolvedIn", "resolved in")}{" "}
                                  {minutesToLabel(a.minutes_to_resolve)}
                                </>
                              )}
                            </p>
                          </div>
                          <span
                            className={cn(
                              "whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium",
                              a.resolved
                                ? "border-green-200 bg-green-100 text-green-800"
                                : "border-red-200 bg-red-100 text-red-800"
                            )}
                          >
                            {a.resolved
                              ? t("patrolReports.stateResolved", "Resolved")
                              : t("patrolReports.stateOpen", "Open")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
